import { nextZoom } from '../components/Stage';

const TYPING = 'input, textarea, select, [contenteditable="true"], [contenteditable="plaintext-only"]';
const NUDGE = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };

// Keyboard shortcuts while focus is inside the editor (typing in a field is left alone):
// ⌘/Ctrl+Z / ⇧⌘Z / Ctrl+Y undo and redo, Delete removes, ⌘D duplicates, ⌘C/⌘X/⌘V copy,
// cut and paste, arrows nudge (Shift ×10), Enter edits a text, ⌘] / ⌘[ arrange (with Shift
// to the front or back), ⌘+ / ⌘− / ⌘0 zoom, Page Up / Page Down go through a carousel's
// pages (also while locked: only viewing), Escape steps back (moving a photo, the settings
// dock, the panel, then the selection); Enter also ends moving a photo.
// 2.13.0: Space plays or pauses a page with videos (also while locked: only watching).
export default function useShortcuts(editor, { locked, scale, dock, playback = null }) {
  return event => {
    if (event.target.closest?.(TYPING) || event.defaultPrevented) return;
    const mod = event.metaKey || event.ctrlKey;
    const key = event.key.length === 1 ? event.key.toLowerCase() : event.key;
    const layer = editor.selected.length === 1 ? editor.selected[0] : null;
    const handled = () => { event.preventDefault(); event.stopPropagation(); };
    if (key === ' ' && !mod && playback?.getState().duration && !event.target.closest?.('button, [role="button"], [role="slider"]')) { handled(); playback.toggle(); return; }
    if (mod && key === 'z') { handled(); if (event.shiftKey) editor.redo(); else editor.undo(); return; }
    if (mod && key === 'y') { handled(); editor.redo(); return; }
    if (mod && (key === '=' || key === '+')) { handled(); editor.setZoom(nextZoom(scale, 1)); return; }
    if (mod && key === '-') { handled(); editor.setZoom(nextZoom(scale, -1)); return; }
    if (mod && key === '0') { handled(); editor.setZoom('fit'); return; }
    if ((key === 'PageUp' || key === 'PageDown') && editor.document?.pages.length > 1) { handled(); editor.stepPage(key === 'PageUp' ? -1 : 1); return; }
    if (editor.cropping && (key === 'Escape' || key === 'Enter')) { handled(); editor.crop(null); return; }
    if (dock?.active && key === 'Escape') { handled(); dock.close(); return; }
    if (key === 'Escape') {
      if (editor.tool) { handled(); editor.setTool(null); return; }
      if (editor.selection.length) { handled(); editor.select([]); }
      return;
    }
    if (mod && key === 'c' && editor.selection.length) { if (editor.copy(editor.selection)) handled(); return; }
    if (locked) return;
    if (mod && key === 'x' && editor.selection.length) { handled(); editor.copy(editor.selection); editor.remove(editor.selection); return; }
    if (mod && key === 'v' && editor.hasClipboard()) { handled(); editor.paste(); return; }
    if ((key === 'Delete' || key === 'Backspace') && editor.selection.length) { handled(); editor.remove(editor.selection); return; }
    if (!layer) return;
    if (mod && key === 'd') { handled(); editor.duplicate(layer.id); return; }
    if (mod && (key === ']' || key === '[')) { handled(); editor.arrange(layer.id, key === ']' ? (event.shiftKey ? 'front' : 'forward') : (event.shiftKey ? 'back' : 'backward')); return; }
    if (key === 'Enter' && layer.type === 'text' && !layer.locked) { handled(); editor.edit(layer.id); return; }
    if (NUDGE[key] && !layer.locked) {
      handled();
      const step = event.shiftKey ? 10 : 1;
      editor.update(layer.id, { x: layer.x + NUDGE[key][0] * step, y: layer.y + NUDGE[key][1] * step }, { key: `${layer.id}:nudge` });
    }
  };
}
